import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useContext, useEffect, useMemo, useState } from 'react';
import PaymentsApi from '../../../../apis/PaymentsApi';
import LoadingCircle from '../../../../components/LoadingCircle/LoadingCircle';
import SnackbarContext from '../../../../contexts/SnackbarContext';
import { PaymentState } from '../../../../generated-types/paymentState';
import useApi from '../../../../lib/useApi';
import DarkPalette from '../../../../theme/DarkPalette';
import CommonStepProps from '../../lib/CommonStepProps';

/**
 * A step to accept payment for the training.
 */
const PaymentStep = ({
  setStepComplete,
  config,
  showError,
  hideTitle,
  moveToNextStep,
}: CommonStepProps) => {
  const paymentsApi = useApi(PaymentsApi);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const options = useMemo(
    () => ({
      fetchClientSecret: () => paymentsApi.createLicenseCheckoutSession(),
    }),
    [paymentsApi]
  );
  const theme = useTheme();
  const [paymentState, setPaymentState] = useState<PaymentState>();
  const snackbar = useContext(SnackbarContext);

  const stripePromise = useMemo(
    () => loadStripe(config.stripeFrontEndPublicKey),
    [config]
  );

  useEffect(() => {
    hideTitle();
  }, [hideTitle]);

  useEffect(() => {
    // Set the payment state
    paymentsApi
      .getClientOnboardingPaymentState()
      .then(setPaymentState)
      .catch(showError);
  }, [paymentsApi, showError]);

  return (
    <Box
      paddingBottom={theme.palette.mode == 'dark' ? '0' : '1em'}
      height="100%"
    >
      {paymentState == PaymentState.NotStarted && (
        <Box
          id="checkout"
          paddingTop={theme.palette.mode == 'dark' ? '0' : '1em'}
          paddingBottom="1em"
          borderRadius="1em"
          sx={{
            backgroundColor: DarkPalette.background?.default,
          }}
        >
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{
              ...options,
              onComplete: () => {
                setPaymentState(PaymentState.WaitingPayment);
                snackbar.setAlert(
                  <Alert severity="success" variant="filled">
                    Successfully submitted payment!
                  </Alert>
                );
                snackbar.triggerSnackbar();
              },
            }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </Box>
      )}
      {paymentState == PaymentState.Failed && (
        <Box
          height="100%"
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          gap="1em"
        >
          <Box display="flex" alignItems="center" gap=".75em">
            <Typography variant="h3">Payment Failed</Typography>
            <ErrorOutlineIcon
              style={{
                fontSize: '2.5em',
              }}
              color="error"
            />
          </Box>
          <Typography
            variant="body1"
            gutterBottom
            textAlign="center"
            maxWidth="50em"
          >
            We couldn&apos;t process your payment. This may be due to
            insufficient funds, an invalid payment method, or an issue with your
            payment provider. Please try again or contact support for
            assistance.
          </Typography>
          <Button
            onClick={() => setPaymentState(PaymentState.NotStarted)}
            variant="contained"
          >
            Try Again
          </Button>
        </Box>
      )}
      {paymentState == PaymentState.Success && (
        <Box
          height="100%"
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          gap="2em"
        >
          <Box display="flex" alignItems="center" gap=".75em">
            <Typography variant="h3">Payment Successful</Typography>
            <CheckCircleOutlineIcon
              style={{
                fontSize: '3em',
              }}
              color="success"
            />
          </Box>
          <Button variant="contained" onClick={moveToNextStep}>
            Next Step
          </Button>
        </Box>
      )}
      {paymentState == PaymentState.WaitingPayment && (
        <Box width="100%" display="flex" justifyContent="center" height="100%">
          <Box
            height="100%"
            maxWidth="80%"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="1em"
          >
            <Box display="flex" alignItems="center" gap="2em">
              <CircularProgress thickness={4} />
              <Typography variant="h3">Processing payment...</Typography>
            </Box>
            <Typography textAlign="center">
              Payment submitted successfully! Once your payment is received,
              this step will be completed automatically. Depending on your
              payment method, this process may complete quickly or take a few
              days.
            </Typography>
            <LoadingButton
              variant="contained"
              loading={refreshLoading}
              endIcon={<RefreshRoundedIcon />}
              onClick={() => {
                setRefreshLoading(true);
                setTimeout(() => {
                  paymentsApi
                    .getClientOnboardingPaymentState()
                    .then((p) => {
                      setPaymentState(p);
                      if (p == PaymentState.Success) setStepComplete();
                    })
                    .catch(showError)
                    .finally(() => setRefreshLoading(false));
                }, 1000);
              }}
            >
              Check for Updates
            </LoadingButton>
          </Box>
        </Box>
      )}
      <LoadingCircle isLoading={paymentState == undefined} />
    </Box>
  );
};

export default PaymentStep;
